import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import arrayMutators from "final-form-arrays";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { ImportsRow } from "./ImportsRow";
import { useCallback, useEffect, useMemo, useState } from "react";
import ImportsStepper from "./ImportsStepper";
import { Form } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  EntryCheckRequest,
  EntryCheckResponse,
  FileColumnMapping,
  ImportJobTypeField,
  ListRequestOfBaseFilter,
  ListResponseOfImportJobTypeField,
  ResponseCode,
  ResponseOfEntryCheckResponse,
  ResponseOfListOfEntryCheckResponse,
} from "services/KnowfullyAdminClient";
import importsUploadStore, {
  importsBulkUploadStore,
} from "stores/importsUploadStore";
import { Color } from "types/colors";
import { SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";

const styles = {
  shadow: {
    boxShadow: "0px 5px 10px -5px rgba(0, 0, 0, 0.35)",
    mb: 2,
  },
};

const ROWS_TO_CHECK = 100;

const ImportsColumnMappingPage = () => {
  const { formatMessage: t } = useIntl();

  const { columns, data } = importsUploadStore.useState((s) => ({
    columns: s.columns ?? [],
    data: s.data,
  }));

  const { registerPromise } = useTrashablePromises();

  const [loaded, setLoaded] = useBoolean(false);
  const [initialized, setInitialized] = useState<
    { entryCheckRequest: EntryCheckRequest; migrationJobTypeFieldId: string }[]
  >([]);
  const [validating, setValidating] = useState<boolean[]>([]);
  const [validated, setValidated] = useState<boolean[]>([]);
  const [required, setRequired] = useState<boolean[]>([]);
  const [evaluated, setEvaluated] = useState<boolean[]>([]);
  const [highlighted, setHighlighted] = useState<boolean[]>([]);
  const [empty, setEmpty] = useState<boolean[]>([]);
  const [excluded, setExcluded] = useState<{ index: number; value: string }[]>(
    []
  );

  const [columnsNotMatched, setColumnNotMatched] = useState<
    string[] | undefined
  >(undefined);

  const [validationMessages, setValidationMessages] = useState(
    columns?.map((x) => t({ id: "notYetEvaluatedMessage" }))
  );

  const [requiredUnassigned, setRequiredUnassigned] = useState<
    ImportJobTypeField[]
  >([]);

  const {
    migrationJobService,
    importsUrl,
    importsProcessUrl,
    importsUploadUrl,
  } = useGlobalContext();
  const navigate = useNavigate();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [importing, setImporting] = useBoolean(false);

  const { id: migrationJobTypeId } = useParams<{ id: string }>();

  //logic for redirecting if page is refreshed.
  useEffect(() => {
    if (!data) {
      navigate(importsUrl());
    }
  }, [data, navigate, importsUrl]);

  const checkableData = data?.slice(
    0,
    ROWS_TO_CHECK > data?.length ? data?.length : ROWS_TO_CHECK
  );

  useTitle(t({ id: "importsStep2Label" }));

  const fetchedMigrationJobTypeFields = useQuery<ListResponseOfImportJobTypeField>(
    ["migrationJobTypeFields", migrationJobTypeId],
    () =>
      migrationJobService.getImportJobTypeFields(
        migrationJobTypeId!,
        ListRequestOfBaseFilter.fromJS({
          sortField: "dataType",
          pageSize: -1,
        })
      )
  );

  const migrationJobTypeFields = fetchedMigrationJobTypeFields.data
    ?.data as ImportJobTypeField[];

  //function for taking the first 100 rows to be evaluated on the backend for entry check.
  const getUniqueValues = useCallback(
    (index: number): any[] => {
      const columnName = columns ? columns[index] : "";

      const valuesToCheck = checkableData?.map(
        (fileElement: any) => fileElement[columnName]
      );

      const set = new Set(valuesToCheck);
      const uniqueValues = Array.from(set);
      return uniqueValues;
    },
    [checkableData, columns]
  );

  const postEvaluation = useCallback(
    (
      index: number,
      response: EntryCheckResponse,
      migrationJobTypeField: ImportJobTypeField
    ) => {
      const validationResult = response.result;
      setValidated((prevState) =>
        prevState.map((x, i) => {
          return i === index ? validationResult! : x;
        })
      );

      setValidationMessages((prevState) =>
        prevState.map((x, i) => {
          return i === index
            ? validationResult
              ? t(
                  { id: "validationCheckSuccessfullMessage" },
                  {
                    column: index + 1,
                  }
                )
              : t(
                  { id: "validationCheckFailedMessage" },
                  {
                    rows: ROWS_TO_CHECK - 1,
                    column: index + 1,
                  }
                )
            : x;
        })
      );

      setValidating((prevState) =>
        prevState.map((x) => {
          return false;
        })
      );

      setHighlighted((prevState) =>
        prevState.map((x, i) => {
          return i === index
            ? !validationResult && migrationJobTypeField?.required!
            : x;
        })
      );

      setEmpty((prevState) =>
        prevState.map((x, i) => {
          return i === index ? !validationResult && response.isEmpty! : x;
        })
      );

      //if has been selected, we should remove from the fields list because you can relate it only once.
      if (validationResult) {
        setExcluded((prevState) =>
          prevState.concat({
            index,
            value: migrationJobTypeField.fieldName!,
          })
        );
      }
    },
    [t]
  );

  const entryCheck = async (value: string, index: number) => {
    setEvaluated((prevState) =>
      prevState.map((x, i) => {
        return !x ? i === index : x;
      })
    );

    setValidating((prevState) =>
      prevState.map((x, i) => {
        return !x ? i === index : false;
      })
    );

    setValidated((prevState) =>
      prevState.map((x, i) => {
        return !x ? i === index : x;
      })
    );

    const uniqueValues = getUniqueValues(index);

    const migrationJobTypeField = migrationJobTypeFields.find(
      (element) => element.id === value
    );

    if (migrationJobTypeField) {
      setRequired((prevState) =>
        prevState.map((x, i) => {
          return i === index ? migrationJobTypeField?.required! : x;
        })
      );

      const request: EntryCheckRequest = new EntryCheckRequest({
        index,
        values: uniqueValues,
        required: migrationJobTypeField.required,
        dataType: migrationJobTypeField.dataType,
      });

      try {
        const response: ResponseOfEntryCheckResponse = await registerPromise(
          migrationJobService.entryCheck(request)
        );

        if (response?.responseCode === ResponseCode.OK) {
          postEvaluation(index, response.data!, migrationJobTypeField);
        }
      } catch (error) {
        console.log(error);
        return { [FORM_ERROR]: translateError(error, t) };
      }
    }
  };

  const initializeValues = () => {
    let initialColumnValues = columns?.map((x) => false);
    setEvaluated(initialColumnValues);
    setValidating(initialColumnValues);
    setRequired(initialColumnValues);
    setHighlighted(initialColumnValues);
    setValidated(initialColumnValues);
    setEmpty(initialColumnValues);
  };

  useEffect(() => {
    if (!loaded) {
      initializeValues();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const postFieldInitialization = (data: EntryCheckResponse[]) => {
      const excludedInitialized = data.map((x) => {
        const migrationJobTypeField = migrationJobTypeFields.find(
          (f) =>
            f.id ===
            initialized.find((i) => i.entryCheckRequest.index === x.index)
              ?.migrationJobTypeFieldId
        );

        return {
          value: migrationJobTypeField?.fieldName!,
          index: x.index!,
          validated: x.result!,
        };
      });

      setRequired((prevState) =>
        prevState.map((x, i) => {
          return !x
            ? initialized.find((z) => z.entryCheckRequest.index === i)
                ?.entryCheckRequest.required ?? false
            : x;
        })
      );

      setHighlighted((prevState) =>
        prevState.map((x, i) => {
          return data.some((z) => z.index === i && !z.result && z.required);
        })
      );

      setEvaluated((prevState) =>
        prevState.map((x, i) => {
          return data.some((z) => z.index === i);
        })
      );

      setValidated((prevState) =>
        prevState.map((x, i) => {
          return data.some((z) => z.index === i && z.result);
        })
      );

      setEmpty((prevState) =>
        prevState.map((x, i) => {
          return data.some((z) => z.index === i && z.isEmpty);
        })
      );

      setValidationMessages((prevState) =>
        prevState.map((x, i) => {
          return data.some((z) => z.index === i && z.result)
            ? t(
                { id: "validationCheckSuccessfullMessage" },
                {
                  column: i + 1,
                }
              )
            : data.some((z) => z.index === i && z.isEmpty)
            ? t({ id: "validationEmptyColumnMessage" })
            : t(
                { id: "validationCheckFailedMessage" },
                {
                  rows: ROWS_TO_CHECK,
                  column: i + 1,
                }
              );
        })
      );

      setExcluded([...excludedInitialized.filter((z) => z.validated)]);
    };

    async function initializeEntries() {
      if (initialized.length && !loaded) {
        const response: ResponseOfListOfEntryCheckResponse = await migrationJobService.entriesCheck(
          initialized.map((x) => x.entryCheckRequest)
        );

        postFieldInitialization(response?.data!);

        setLoaded.setTrue();
      }
    }

    initializeEntries();
  }, [
    initialized,
    loaded,
    migrationJobService,
    migrationJobTypeFields,
    setLoaded,
    t,
  ]);

  const init = useMemo(() => {
    let migrationTypesInit: any = undefined;
    if (!fetchedMigrationJobTypeFields.isLoading) {
      let initializedArray: {
        migrationJobTypeFieldId: string;
        entryCheckRequest: EntryCheckRequest;
      }[] = [];
      columns?.forEach((column, index) => {
        const migrationJobTypeField = migrationJobTypeFields?.find(
          (x) => x.fieldName === column
        );
        if (migrationJobTypeField) {
          migrationTypesInit = {
            ...migrationTypesInit,
            [`migrationTypeId_${index}`]: migrationJobTypeField?.id,
          };

          initializedArray.push({
            entryCheckRequest: new EntryCheckRequest({
              index,
              values: getUniqueValues(index),
              required: migrationJobTypeField?.required,
              dataType: migrationJobTypeField?.dataType,
            }),
            migrationJobTypeFieldId: migrationJobTypeField?.id!,
          });
        }

        setInitialized(initializedArray);
      });

      if (!migrationTypesInit) {
        setColumnNotMatched(migrationJobTypeFields.map((x) => x.fieldName!));

        return;
      } else {
        setColumnNotMatched(undefined);
      }

      return {
        columns,
        ...migrationTypesInit,
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, migrationJobTypeFields]);

  const hasError = useCallback(
    (index: number) => {
      return (
        index > -1 &&
        !validating[index] &&
        !validated[index] &&
        evaluated[index] &&
        !empty[index]
      );
    },
    [empty, evaluated, validated, validating]
  );

  const notMapped = useCallback(
    (index: number) => {
      return (
        index > -1 &&
        !validating[index] &&
        !validated[index] &&
        !evaluated[index] &&
        loaded
      );
    },
    [evaluated, loaded, validated, validating]
  );

  useEffect(() => {
    const requiredConditionally = (field: ImportJobTypeField) => {
      const fieldIndex = columns.indexOf(field.fieldName!);

      return (
        (hasError(fieldIndex) || notMapped(fieldIndex)) &&
        field.requiredIfFieldsMissing?.every((f) => {
          const index = columns.indexOf(f);
          return hasError(index) || notMapped(index);
        })
      );
    };

    const isExcluded = (field: ImportJobTypeField) => {
      return excluded.map((e) => e.value).includes(field.fieldName!);
    };

    setRequiredUnassigned(
      migrationJobTypeFields?.filter(
        (f) => (f.required || requiredConditionally(f)) && !isExcluded(f)
      )
    );
  }, [columns, excluded, hasError, loaded, migrationJobTypeFields, notMapped]);

  const onSubmit = (values: any) => {
    let propertyNames = Object.getOwnPropertyNames(values);

    let columnMappingValues: FileColumnMapping[] = [];

    propertyNames.forEach((propertyName: string) => {
      const index = parseInt(propertyName.split("_")[1]);

      if (
        propertyName.includes("migrationTypeId_") &&
        (validated[index] ||
          (!required[index] && !hasError(index) && !notMapped(index)))
      ) {
        const name = values.columns[index];
        const value = values[propertyName];

        const migrationJobTypeField = migrationJobTypeFields.find(
          (element) => element.id === value
        );

        let columnMapping = new FileColumnMapping({
          importJobTypeFieldId: migrationJobTypeField?.id!,
          importJobTypeId: migrationJobTypeField?.importJobTypeId!,
          dataType: migrationJobTypeField?.dataType!,
          columnIndex: index,
          columnName: name,
          required: migrationJobTypeField?.required!,
        });

        columnMappingValues.push(columnMapping);
      }
    });

    importsUploadStore.update((s) => {
      s.mappings = columnMappingValues;
    });

    importsBulkUploadStore.update((s) => {
      s.uploadCompleted = false;
    });

    navigate(
      importsProcessUrl(migrationJobTypeId, new Date().toISOString())
    );
  };

  const restartField = (index: number) => {
    setEvaluated((prevState) =>
      prevState.map((x, i) => {
        return i === index ? false : x;
      })
    );

    setValidated((prevState) =>
      prevState.map((x, i) => {
        return i === index ? false : x;
      })
    );

    setRequired((prevState) =>
      prevState.map((x, i) => {
        return i === index ? false : x;
      })
    );

    setValidationMessages((prevState) =>
      prevState.map((x, i) => {
        return i === index ? t({ id: "notYetEvaluatedMessage" }) : x;
      })
    );

    setHighlighted((prevState) =>
      prevState.map((x, i) => {
        return i === index ? false : x;
      })
    );

    setExcluded((prevState) => prevState.filter((x) => x.index !== index));
  };

  return (
    <>
      <Typography variant="h4">
        <T id="importsStep2Label" />
      </Typography>
      <Box mb={2}></Box>
      <ImportsStepper activeStep={1} />
      <Form
        initialValues={init}
        onSubmit={onSubmit}
        mutators={{
          ...arrayMutators,
        }}
        render={({ handleSubmit, submitting }) => {
          return (
            <form onSubmit={handleSubmit}>
              {!columnsNotMatched ? (
                <>
                  <DialogContent>
                    <Grid
                      container
                      spacing={3}
                      style={{ backgroundColor: Color.WhiteSmoke }}
                    >
                      <Grid id="title" item xs={4}>
                        <Typography
                          variant="h6"
                          style={{ marginBottom: "5px" }}
                        >
                          {t({ id: "fileColumnNameLabel" })}
                        </Typography>
                      </Grid>
                      <Grid id="title" item xs={4}></Grid>
                      <Grid id="title" item xs={4}>
                        <Typography
                          variant="h6"
                          style={{ marginBottom: "5px" }}
                        >
                          {t({ id: "mappingLabel" })}
                        </Typography>
                      </Grid>
                    </Grid>
                    <hr
                      style={{ color: Color.Gainsboro, marginBottom: "40px" }}
                    />

                    <FieldArray name="columns">
                      {({ fields }) =>
                        fields.map((name: string, index: any) => {
                          return (
                            <ImportsRow
                              key={`${name}_${index}`}
                              name={name}
                              evaluated={evaluated[index]}
                              excluded={excluded}
                              highlighted={highlighted[index]}
                              loaded={loaded}
                              index={index}
                              required={required[index]}
                              validated={validated[index]}
                              validating={validating[index]}
                              empty={empty[index]}
                              validationMessage={validationMessages[index]}
                              entryCheck={entryCheck}
                              restartField={restartField}
                            />
                          );
                        })
                      }
                    </FieldArray>
                  </DialogContent>
                  <Dialog open={importing} disableEnforceFocus>
                    <DialogTitle>
                      <T id="importsProcessFileTitle" />
                    </DialogTitle>
                    <DialogContent dividers>
                      <T id="importsProcessFileQuestion" />
                    </DialogContent>
                    <DialogActions>
                      <Button
                        autoFocus
                        onClick={() => setImporting.setFalse()}
                        color="primary"
                      >
                        <T id="cancelButton" />
                      </Button>
                      <Button
                        color="primary"
                        onClick={(e) => {
                          setImporting.setFalse();
                          handleSubmit(e);
                        }}
                      >
                        {t({ id: "processButton" })}
                      </Button>
                    </DialogActions>
                  </Dialog>
                  {loaded ? (
                    <Box sx={styles.shadow}>
                      {requiredUnassigned.length ? (
                        <Alert severity="warning">
                          <AlertTitle>
                            {t({ id: "pendingAssignmentLabel" })}
                          </AlertTitle>
                          {t({ id: "pendingAssignmentMessageLabel" })}
                          <strong>
                            {requiredUnassigned
                              .map((x) => x.fieldName)
                              .join(" - ")}
                          </strong>
                        </Alert>
                      ) : (
                        <Alert severity="success">
                          <AlertTitle>Success</AlertTitle>
                          {t({ id: "successAssignmentMessageLabel" })}
                        </Alert>
                      )}
                    </Box>
                  ) : (
                    <div style={{ textAlign: "center" }}>
                      <CircularProgress size={32} />
                    </div>
                  )}
                </>
              ) : (
                <Box sx={styles.shadow}>
                  <Alert severity="error">
                    <AlertTitle>{t({ id: "anyColumnMatchTitle" })}</AlertTitle>
                    {t({ id: "anyColumnMatchErrorMessageLabel" })}
                    <strong>
                      {columnsNotMatched.length > 0 ? (
                        columnsNotMatched.join(" - ")
                      ) : (
                        <div style={{ color: Color.Red }}>
                          {t({ id: "notColumnsHasBeenDefinedInTheImport" })}
                        </div>
                      )}
                    </strong>
                  </Alert>
                </Box>
              )}

              <DialogActions>
                {submitting && <CircularProgress size={24} />}
                <Button
                  disabled={submitting}
                  onClick={() => navigate(importsUploadUrl())}
                >
                  {t({ id: "cancelButton" })}
                </Button>

                <Button
                  color="primary"
                  disabled={
                    submitting ||
                    validating?.some((x) => x) ||
                    highlighted?.some((x) => x) ||
                    validated?.every((x) => x === false) ||
                    requiredUnassigned.length > 0
                  }
                  onClick={() => setImporting.setTrue()}
                >
                  {t({ id: "processButton" })}
                </Button>
              </DialogActions>
              <SnackbarMessage
                snackbarMessage={snackbarMessage}
                setSnackbarMessage={setSnackbarMessage}
              />
            </form>
          );
        }}
      />
    </>
  );
};

export default ImportsColumnMappingPage;

